import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ATTRIBUTE_KINDS,
  ATTRIBUTE_TYPES,
  DISPLAY_FORMATS,
  AttributeKind,
  AttributeType,
  DisplayFormat,
} from '@/entities/attributes';
import { ComboboxItem } from '@ibsys/kit';

/**
 * Опции типов данных атрибута для Combobox и подписи типа по его значению.
 * @param params.withStructure — включать вариант «Структура»: смена типа существующего атрибута
 * на структуру и обратно запрещена, поэтому в режиме редактирования вариант скрывается
 */
export const useGetAttributeTypes = ({ withStructure = true }: { withStructure?: boolean } = {}) => {
  const { t } = useTranslation('attributes');

  const options = useMemo(
    () =>
      [
        {
          value: ATTRIBUTE_TYPES.INTEGER,
          label: t('type.integer'),
        },
        {
          value: ATTRIBUTE_TYPES.DECIMAL,
          label: t('type.decimal'),
        },
        {
          value: ATTRIBUTE_TYPES.TEXT,
          label: t('type.text'),
        },
        {
          value: ATTRIBUTE_TYPES.SELECT,
          label: t('type.select'),
        },
        {
          value: ATTRIBUTE_TYPES.MULTISELECT,
          label: t('type.multiselect'),
        },
        {
          value: ATTRIBUTE_TYPES.CHECKBOX,
          label: t('type.checkbox'),
        },
        {
          value: ATTRIBUTE_TYPES.DATE,
          label: t('type.date'),
        },
        {
          value: ATTRIBUTE_TYPES.TIMESTAMP,
          label: t('type.timestamp'),
        },
        ...(withStructure
          ? ([
              {
                type: 'separator',
                value: 'separator-between-structure',
              },
              {
                value: ATTRIBUTE_TYPES.STRUCTURE,
                label: t('type.structure'),
              },
            ] satisfies ComboboxItem[])
          : []),
      ] satisfies ComboboxItem[],
    [t, withStructure],
  );

  const getLabelByType = (type: AttributeType) => {
    return options.find((option) => option.value === type)?.label;
  };

  return { options, getLabelByType };
};

export const useGetDisplayFormats = () => {
  const { t } = useTranslation('attributes');

  const options = useMemo(
    () =>
      [
        {
          value: DISPLAY_FORMATS.SECOND,
          label: t('displayFormat.seconds'),
        },
        {
          value: DISPLAY_FORMATS.PERCENT,
          label: t('displayFormat.percent'),
        },
      ] satisfies ComboboxItem[],
    [t],
  );

  const getLabelByFormat = (format: DisplayFormat) => {
    return options.find((option) => option.value === format)?.label;
  };

  return { options, getLabelByFormat };
};

export const useGetAttributeKinds = () => {
  const { t } = useTranslation('attributes');

  const options = useMemo(
    () =>
      [
        {
          value: ATTRIBUTE_KINDS.SYSTEM,
          label: t('attributeKind.SYSTEM'),
        },
        {
          value: ATTRIBUTE_KINDS.COMPUTED_TRANSCRIBATION,
          label: t('attributeKind.COMPUTED_TRANSCRIBATION'),
        },
        {
          value: ATTRIBUTE_KINDS.COMPUTED_RAW,
          label: t('attributeKind.COMPUTED_RAW'),
        },
        {
          value: ATTRIBUTE_KINDS.EXPERT_REPORT_RATING,
          label: t('attributeKind.EXPERT_REPORT_RATING'),
        },
        {
          value: ATTRIBUTE_KINDS.ANALYTICS,
          label: t('attributeKind.ANALYTICS'),
        },
      ] satisfies ComboboxItem[],
    [t],
  );

  const getLabelByKind = (kind: AttributeKind) => {
    return options.find((option) => option.value === kind)?.label;
  };

  return { options, getLabelByKind };
};
